import { Monitor, Moon, Sun } from 'lucide-react';
import { useRef } from 'react';

import { useTheme, type ThemePreference } from '@/lib/theme/theme-context';
import { cn } from '@/lib/utils';

const OPTIONS: readonly {
  readonly value: ThemePreference;
  readonly label: string;
  readonly Icon: typeof Sun;
}[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

/**
 * Three-way theme control.
 *
 * `System` is offered as its own option rather than inferred from a two-state
 * toggle, because the two are not the same setting: picking Dark at night on a
 * machine that switches itself at sunrise should keep the console dark in the
 * morning.
 *
 * A radiogroup rather than three buttons: exactly one is selected at a time,
 * which is what lets a screen reader announce "Dark, selected, 2 of 3", and
 * what makes the arrow keys work the way they do everywhere else.
 */
export function ThemeToggle({ className }: { readonly className?: string }) {
  const { preference, setPreference } = useTheme();
  const groupRef = useRef<HTMLDivElement>(null);

  // Selection and focus move together, which is what "roving tabindex" means.
  // Changing the selected radio without moving focus strands the keyboard on a
  // control that has just left the tab order.
  const select = (value: ThemePreference) => {
    setPreference(value);
    groupRef.current?.querySelector<HTMLButtonElement>(`[data-theme-option="${value}"]`)?.focus();
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="Colour theme"
      // Neutral tokens rather than the `sidebar-*` set: this renders in the
      // sidebar footer and on the sign-in card, and the two surfaces are close
      // enough in both themes that one styling covers them.
      className={cn('flex items-center gap-1 rounded-lg border border-border bg-card p-1', className)}
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const isSelected = preference === value;

        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={label}
            title={label}
            data-theme-option={value}
            // Only the selected control is in the tab order; the arrow keys
            // move within the group. Three separate tab stops for one setting
            // is what a radiogroup exists to avoid.
            tabIndex={isSelected ? 0 : -1}
            onClick={() => {
              setPreference(value);
            }}
            onKeyDown={(event) => {
              const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
              const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
              if (!forward && !back) return;

              event.preventDefault();
              const index = OPTIONS.findIndex((option) => option.value === preference);
              const step = forward ? 1 : -1;
              const next = OPTIONS[(index + step + OPTIONS.length) % OPTIONS.length];
              if (next !== undefined) select(next.value);
            }}
            className={cn(
              // A fixed square per option: sized by its icon alone, each one
              // was a 14px sliver that was hard to hit and read as cramped.
              'flex size-7 items-center justify-center rounded-md transition-colors',
              isSelected
                ? 'bg-accent text-accent-foreground shadow-xs'
                : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
            )}
          >
            <Icon aria-hidden="true" className="size-4" />
          </button>
        );
      })}
    </div>
  );
}
